import { useTranslation } from '@/lib/i18n';
import { DataTable, Skeleton, type DataTableColumn } from '@/components/ui';
import { formatKstDateTime, parseChanges, type HistoryChange } from '../lib/partnerFormat';
import type { OpenApiPartnerHistory } from '../types/openApiPartner';

const ACTION_LABEL: Record<string, string> = {
  CREATED: '등록',
  UPDATED: '수정',
  KEY_REISSUED: '키 재발급',
  DEACTIVATED: '사용 중지',
  ACTIVATED: '사용 재개',
  DELETED: '삭제',
};

const FIELD_LABEL: Record<string, string> = {
  partnerName: '제휴사명',
  allowedIps: '허용 IP',
  validFrom: '사용 시작',
  validTo: '사용 종료',
  memo: '메모',
  active: '사용 여부',
  partnerKey: '제휴사 키',
  key: '쇼핑몰 키',
  deleted: '삭제',
};

const ISO = /^\d{4}-\d{2}-\d{2}T/;

/** 변경값 표시 — 시각은 KST, true/false 는 예/아니오, IP 는 쉼표 뒤 띄움, 없음은 "없음". */
const formatValue = (field: string, v: string | null): string => {
  if (v == null || v === '') return '없음';
  if (ISO.test(v)) return formatKstDateTime(v) ?? v;
  if (v === 'true' || v === 'false') {
    if (field === 'active') return v === 'true' ? '사용' : '중지';
    return v === 'true' ? '예' : '아니오';
  }
  if (field === 'allowedIps') return v.split(',').join(', ');
  return v;
};

export const describeChange = (c: HistoryChange): string =>
  `${FIELD_LABEL[c.field] ?? c.field}: ${formatValue(c.field, c.before)} → ${formatValue(c.field, c.after)}`;

interface PartnerHistoryTableProps {
  histories: OpenApiPartnerHistory[] | undefined;
  loading: boolean;
  errorMessage?: string | null;
}

/** 이력 탭 — 시각, 작업자, 작업 종류, 변경 내용("필드: 이전 → 이후"), 사유. BE 가 최신순으로 준다. */
export const PartnerHistoryTable = ({ histories, loading, errorMessage }: PartnerHistoryTableProps) => {
  const { t } = useTranslation('common');
  if (loading) return <Skeleton className="h-24 w-full" />;
  if (errorMessage) {
    return (
      <p role="alert" className="text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
        {errorMessage}
      </p>
    );
  }
  const columns: DataTableColumn<OpenApiPartnerHistory>[] = [
    {
      key: 'createdAt',
      header: t('openApiPartners.history.at', '시각'),
      width: 140,
      cell: (h) => <span className="tabular-nums">{formatKstDateTime(h.createdAt) ?? '—'}</span>,
    },
    { key: 'actor', header: t('openApiPartners.history.actor', '작업자'), width: 140, cell: (h) => h.actorId ?? '—' },
    {
      key: 'action',
      header: t('openApiPartners.history.action', '작업'),
      width: 90,
      cell: (h) => t(`openApiPartners.actionLabel.${h.action}`, ACTION_LABEL[h.action] ?? h.action),
    },
    {
      key: 'changes',
      header: t('openApiPartners.history.changes', '변경 내용'),
      cell: (h) => {
        const changes = parseChanges(h.changesJson);
        if (changes.length === 0) return '—';
        return (
          <ul className="flex flex-col gap-0.5" data-testid="history-changes">
            {changes.map((c) => (
              <li key={c.field} className="break-all">
                {describeChange(c)}
              </li>
            ))}
          </ul>
        );
      },
    },
    { key: 'reason', header: t('openApiPartners.history.reason', '사유'), width: 200, cell: (h) => h.reason ?? '—' },
  ];
  return (
    <DataTable
      columns={columns}
      rows={histories ?? []}
      rowKey={(h) => h.historyId}
      ariaLabel={t('openApiPartners.history.title', '변경 이력')}
      emptyState={t('openApiPartners.history.empty', '변경 이력이 없습니다.')}
    />
  );
};
